import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import AOS from 'aos';
import { initGlobe } from './components/initGlobe';
import { createSignaturePad } from './components/signaturePad';
import { createPhoneUtils } from './components/phone';
import { createSheetStore } from './alpine/sheet';
import { taxFilter } from './alpine/taxFilter';
import { createBucketStore } from './alpine/bucketStore';
import { createRequestStore } from './alpine/requestStore';
import { createAcceptStatusStore } from './alpine/acceptStatusStore';
import { createRequestForm } from './alpine/requestForm';
import { dashboardStore } from './alpine/dashboardStore';
import { hairMBTI } from './alpine/hairMBTI';
import { shampooMBTI } from './alpine/shampooMBTI';
import { hairScholarQuiz } from './alpine/hairQUIZ';

Alpine.plugin(collapse);

async function fetchJSON(url) {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Request failed: ${res.status} ${res.statusText}\n${text}`);
  }
  return res.json();
}

async function renderShortcode(shortcode) {
  const res = await fetch('/wp-json/dcraft/v1/shortcode', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shortcode }),
    credentials: 'same-origin',
  });
  const data = await res.json();
  return data.html || '';
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// 가격 등 관리자가 줄바꿈(<br>)만 넣는 짧은 텍스트용:
// 전체를 escape 한 뒤 <br> 계열만 되살려 목록/썸네일(원문 출력)과 표시를 맞춘다.
function escapeHtmlAllowBr(value = '') {
  return escapeHtml(value).replace(/&lt;br\s*\/?&gt;/gi, '<br />');
}

function stripHtml(value = '') {
  return String(value)
    .replace(/<[^>]*>/g, '')
    .trim();
}

function getFirstProductTerm(post) {
  const terms = post?._embedded?.['wp:term'];
  if (!Array.isArray(terms)) return null;

  return terms.flat().find((term) => term?.taxonomy === 'dc_product_taxonomy');
}

function initModalPostLinks() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-modal-post-id]');
    if (!el) return;

    // 링크/버튼 내부 클릭이면 중복 방지(원하면 제거 가능)
    const interactive = e.target.closest(
      'a, button, input, textarea, select, label'
    );
    if (interactive) return;

    e.preventDefault();

    const id = Number(el.getAttribute('data-modal-post-id'));
    if (!id) return;

    const size = el.dataset.modalSize || 'lg';

    window.Alpine?.store('modal')?.showPost({
      id,
      postType: 'posts', // 필요하면 'portfolio' 등으로 변경
      size: size,
    });
  });

  // 키보드 접근성(Enter/Space로 열기)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const el = e.target?.closest?.('[data-modal-post-id]');
    if (!el) return;

    e.preventDefault();

    const id = Number(el.getAttribute('data-modal-post-id'));
    if (!id) return;

    window.Alpine?.store('modal')?.showPost({
      id,
      postType: 'posts',
      size: 'lg',
    });
  });
}

function initModalProductCartButtons() {
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest('[data-modal-product-cart-token]');
      if (!el) return;

      e.preventDefault();

      const token = el.dataset.modalProductCartToken || '';
      const title = el.dataset.modalProductCartTitle || '제품';
      if (!token) return;

      window.Alpine?.store('cart')?.addToken(token);
      window.Alpine?.store('toast')?.show?.(
        `${title} 상담요청 담았어요.`,
        'success'
      );
    },
    true
  );
}

export function initAlpine() {
  window.Alpine = Alpine;

  // 인라인 Alpine 컴포넌트(동의서/협찬/시술 확인)에서 스프레드해 쓰는 서명 캔버스 공통 로직.
  window.qrshopSignaturePad = createSignaturePad;

  // 연락처 정규화·검증(스키마 폼 공용, PHP qrshop_normalize_phone() 과 같은 규칙).
  window.qrshopPhone = createPhoneUtils();

  Alpine.store('ui', {
    menuOpen: false,
    openMenu() {
      this.menuOpen = true;
    },
    closeMenu() {
      this.menuOpen = false;
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
  });

  // toast store
  Alpine.store('toast', {
    open: false,
    message: '',
    type: 'info', // 'info' | 'success' | 'warning' | 'error'
    timeoutId: null,
    duration: 2000,

    show(message, type = 'info', duration) {
      this.message = message;
      this.type = type;
      this.open = true;

      // 기존 타이머 정리
      if (this.timeoutId) clearTimeout(this.timeoutId);

      const ms = typeof duration === 'number' ? duration : this.duration;

      this.timeoutId = setTimeout(() => {
        this.close();
      }, ms);
    },

    close() {
      this.open = false;
      // 메시지는 닫힌 뒤 유지/삭제 취향인데 보통은 유지해도 문제 없음
      // this.message = '';
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },
  });

  // ✅ modal store
  Alpine.store('modal', {
    open: false,
    title: '',
    content: '', // HTML 문자열(권장) or plain text
    type: 'default', // 'default' | 'info' | 'success' | 'warning' | 'error'
    loading: false,

    // 옵션
    size: 'md', // 'sm' | 'md' | 'lg' | 'xl'
    closeOnBackdrop: true,
    closeOnEsc: true,

    _scrollY: 0,

    // body scroll lock (menu랑 겹치면 "카운트 방식"이 더 좋지만, 일단 단순 버전)
    lockScroll() {
      this._scrollY = window.scrollY || 0;
      document.documentElement.style.top = `-${this._scrollY}px`;
      document.documentElement.classList.add('is-modal-open');
    },
    unlockScroll() {
      document.documentElement.classList.remove('is-modal-open');
      document.documentElement.style.top = '';
      window.scrollTo(0, this._scrollY);
    },

    show: async function ({
      title = '',
      content = '',
      type = 'default',
      size = 'md',
      closeOnBackdrop = true,
    } = {}) {
      const protectedTypes = [
        'cart',
        'call',
        'request',
        'survey',
        'survey-completion',
      ];

      this.title = title;
      this.type = type;
      this.size = size;
      this.closeOnBackdrop = protectedTypes.includes(type)
        ? false
        : closeOnBackdrop;

      this.loading = false;

      // ✅ shortcode 타입일 때만 렌더링
      if (type === 'shortcode') {
        this.loading = true;

        // content에 숏코드 문자열이 들어오면 그대로 사용
        // (content가 비어있다면 title에 맞춰 기본 숏코드도 가능)
        const shortcode = content || '[dc_hair_mbti]';

        const html = await renderShortcode(shortcode);
        this.content = html;

        this.loading = false;
      } else {
        // ✅ 나머지는 그대로
        this.content = content;
      }

      this.open = true;
    },

    async showPost({
      id,
      postType = 'posts', // 'posts' | 'pages' | 'portfolio' 등 CPT slug
      endpointBase = '/wp-json/wp/v2',
      titleFromPost = true,
      render = null, // (postJson) => htmlString 커스텀 렌더러
      size = 'lg',
      // 샵 products 리피터 오버라이드 값 (title, price, site, siteHidden)
      // 리스트/썸네일과 모달 표시를 일치시키기 위해 REST 원본 값보다 우선 적용
      overrides = null,
    } = {}) {
      if (!id) throw new Error('modal.showPost: id is required');

      this.title = '불러오는 중…';
      this.content = '';
      this.type = 'default';
      this.size = size;
      this.loading = true;
      this.open = true;
      this.postType = postType;
      // this.lockScroll();

      try {
        let url = '';
        if (postType === 'zodiac' || postType === 'horoscope') {
          url = `${endpointBase}`;
        } else {
          url = `${endpointBase}/${postType}/${id}?_embed=1`;
        }
        const post = await fetchJSON(url);

        let beforeHTML = '';
        let afterHTML = '';
        let defaultHtml = '';

        if (this.postType === 'dc_product') {
          const o = overrides || {};
          const rawOverrideTitle = o.title != null ? String(o.title) : null;
          const title =
            rawOverrideTitle != null
              ? escapeHtmlAllowBr(rawOverrideTitle)
              : (post?.title?.rendered ?? '');
          const featuredImage =
            post?._embedded?.['wp:featuredmedia']?.[0]?.source_url ?? '';
          const productTerm = getFirstProductTerm(post);
          const brand = productTerm?.name ?? '';
          let price = o.price != null ? o.price : (post?.acf?.price ?? '');
          if (String(price).trim() === '-') price = '';
          const site = o.site != null ? o.site : (post?.acf?.site ?? '');
          const siteHidden =
            o.siteHidden != null
              ? Boolean(o.siteHidden)
              : Boolean(post?.acf?.site_hidden);
          const useCart =
            window.QRSHOP_BASE?.useCart === true &&
            Boolean(document.body?.dataset?.qrshopLoc);
          const cartTitle = stripHtml(rawOverrideTitle ?? title) || '제품';
          const cartToken = `p:${cartTitle}`;

          beforeHTML = `
            <div class="modal-product__head">
              ${
                featuredImage
                  ? `<img class="modal-product__image" src="${escapeHtml(featuredImage)}" alt="${stripHtml(title)}" />`
                  : `<div class="modal-product__image modal-product__image--empty">No Image</div>`
              }
              <div class="modal-product__meta">
                ${
                  brand
                    ? `<span class="modal-product__brand">${escapeHtml(brand)}</span>`
                    : ''
                }
                <h2 class="modal-product__title">${title}</h2>
                ${
                  price
                    ? `<div class="modal-product__price">${escapeHtmlAllowBr(price)}</div>`
                    : ''
                }
              </div>
            </div>
          `;
          afterHTML = `
            <div class="modal-product__actions">
              ${
                site && !siteHidden
                  ? `<a href="${escapeHtml(site)}" target="_blank" rel="noopener noreferrer" class="flex-1 dc-btn dc-btn--outline dc-btn--bg dc-btn--sm px-6 h-10">상세 정보</a>`
                  : ''
              }
              ${
                useCart
                  ? `<button
                      type="button"
                      class="flex-1 dc-btn dc-btn--outline dc-btn--sm h-10"
                      data-modal-product-cart-token="${escapeHtml(cartToken)}"
                      data-modal-product-cart-title="${escapeHtml(cartTitle)}"
                    >상담요청</button>`
                  : ''
              }
            </div>
          `;
          defaultHtml = `
            <article class="modal-post modal-product">
              ${beforeHTML}
              <div class="modal-product__content wp-content">${post?.content?.rendered ?? ''}</div>
              ${afterHTML}
            </article>
          `;
          this.title = titleFromPost ? '제품 소개' : this.title;
        } else if (this.postType === 'zodiac') {
          defaultHtml = `
          <article class="modal-post">
          ${post?.fortune?.overall ? '<div class="relative -top-4 text-black font-medium">' + post?.fortune?.overall + '</div>' : ''}
          ${post?.fortune?.health ? '<h3 class="text-lg font-semibold my-0">건강운</h3>' : ''}
          ${post?.fortune?.health ? '<p>' + post?.fortune?.health + '</p>' : ''}
          ${post?.fortune?.wealth ? '<h3 class="text-lg font-semibold my-0">재물운</h3>' : ''}
          ${post?.fortune?.wealth ? '<p>' + post?.fortune?.wealth + '</p>' : ''}
          ${post?.fortune?.love ? '<h3 class="text-lg font-semibold my-0">애정운</h3>' : ''}
          ${post?.fortune?.love ? '<p>' + post?.fortune?.love + '</p>' : ''}
          <ul class="flex gap-2 list-none m-0 p-0">
            <li class="flex-1 border border-line rounded-lg px-1 py-3 flex flex-col items-center m-0 gap-2"><strong class="text-black font-semibold">${post?.fortune?.lucky_item}</strong><span class="text-xs text-gray-500">행운의 아이템</span></li>
            <li class="flex-1 border border-line rounded-lg px-1 py-3 flex flex-col items-center m-0 gap-2"><strong class="text-black font-semibold">${post?.fortune?.lucky_color}</strong><span class="text-xs text-gray-500">행운의 컬러</span></li>
          </ul>
          </article>
          `;
          this.title = post?.zodiac_name ?? '';
        } else if (this.postType === 'horoscope') {
          defaultHtml = `
          <article class="modal-post">
            ${post?.fortune?.overall ? '<div class="relative text-black -top-4 font-medium">' + post?.fortune?.overall + '</div>' : ''}
            ${post?.fortune?.health ? '<h3 class="text-lg font-semibold my-0">건강운</h3>' : ''}
            ${post?.fortune?.health ? '<p>' + post?.fortune?.health + '</p>' : ''}
            ${post?.fortune?.wealth ? '<h3 class="text-lg font-semibold my-0">재물운</h3>' : ''}
            ${post?.fortune?.wealth ? '<p>' + post?.fortune?.wealth + '</p>' : ''}
            ${post?.fortune?.love ? '<h3 class="text-lg font-semibold my-0">애정운</h3>' : ''}
            ${post?.fortune?.love ? '<p>' + post?.fortune?.love + '</p>' : ''}
            <ul class="flex gap-2 list-none m-0 p-0">
              <li class="flex-1 border border-line rounded-lg px-1 py-3 flex flex-col items-center m-0 gap-2"><strong class="text-black font-semibold">${post?.fortune?.lucky_number}</strong><span class="text-xs text-gray-500">행운의 숫자</span></li>
              <li class="flex-1 border border-line rounded-lg px-1 py-3 flex flex-col items-center m-0 gap-2"><strong class="text-black font-semibold">${post?.fortune?.lucky_item}</strong><span class="text-xs text-gray-500">행운의 아이템</span></li>
              <li class="flex-1 border border-line rounded-lg px-1 py-3 flex flex-col items-center m-0 gap-2"><strong class="text-black font-semibold">${post?.fortune?.lucky_color}</strong><span class="text-xs text-gray-500">행운의 컬러</span></li>
            </ul>
          </article>
          `;
          this.title = post?.sign_name ?? '';
        } else {
          defaultHtml = `<article class="modal-post">${post?.content?.rendered ?? ''}</article>`;
          this.title = post?.title?.rendered ?? '';
        }

        this.content =
          typeof render === 'function' ? render(post) : defaultHtml;
      } catch (e) {
        this.title = '오류';
        this.content = `<p>콘텐츠를 불러오지 못했습니다.</p>`;
        this.type = 'error';
        // 원인 디버깅은 콘솔로
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    close() {
      if (!this.open) return;
      this.open = false;
      this.loading = false;
      // this.unlockScroll();
    },

    onBackdrop() {
      if (this.closeOnBackdrop) this.close();
    },

    onKeydown(e) {
      if (!this.open) return;
      if (!this.closeOnEsc) return;
      if (e.key === 'Escape') this.close();
    },
  });

  Alpine.store(
    'sheet',
    createSheetStore({
      endpointBase: '/wp-json/dcraft/v1',
      closeOnBackdrop: true,
      closeOnEsc: true,
      fetchJSON, // ✅ 이거 필수
    })
  );

  Alpine.data('guidePicker', (groupCount = 0) => ({
    selected: Array.from({ length: groupCount }, () => null),

    get isFinished() {
      return (
        this.selected.length > 0 &&
        this.selected.every((v) => Number.isInteger(v))
      );
    },

    select(groupIndex, itemIndex) {
      // ✅ 이미 선택된 버튼이면 해제
      if (this.selected[groupIndex] === itemIndex) {
        this.selected[groupIndex] = null;
        return;
      }

      // ✅ 아니면 해당 버튼 활성화
      this.selected[groupIndex] = itemIndex;
    },

    isActive(groupIndex, itemIndex) {
      return this.selected[groupIndex] === itemIndex;
    },
  }));

  const PREFIX = window.QRSHOP_BASE.prefix;
  Alpine.store(
    'cart',
    createBucketStore({
      storage: window.localStorage,
      prefix: PREFIX,
      key: 'cart:items',
      allowed: ['d', 's', 'p'], // d: drink, s: service, product
    })
  );
  Alpine.store(
    'call',
    createBucketStore({
      storage: window.localStorage,
      prefix: PREFIX,
      key: 'call:items',
      allowed: ['c'], // c: call
    })
  );

  // contentViewer
  window.contentViewer = function ({
    postType = 'pages',
    endpointBase = '/wp-json/wp/v2',
    useCache = true,
    initiallyOpen = false,
  } = {}) {
    return {
      loading: false,
      error: '',
      open: initiallyOpen,

      query: {
        id: null,
        slug: '',
      },

      item: {
        id: null,
        slug: '',
        title: '',
        content: '',
        raw: null,
      },

      cache: new Map(),

      makeCacheKey({ id = null, slug = '', type = postType } = {}) {
        if (id) return `${type}:id:${id}`;
        if (slug) return `${type}:slug:${slug}`;
        return `${type}:empty`;
      },

      normalizeItem(page, fallbackSlug = '') {
        return {
          id: page?.id ?? null,
          slug: page?.slug ?? fallbackSlug ?? '',
          title: page?.title?.rendered ?? '',
          content: page?.content?.rendered ?? '',
          raw: page ?? null,
        };
      },

      async fetchById(id) {
        return await fetchJSON(`${endpointBase}/${postType}/${id}`);
      },

      async fetchBySlug(slug) {
        const list = await fetchJSON(
          `${endpointBase}/${postType}?slug=${encodeURIComponent(slug)}`
        );
        return Array.isArray(list) ? list[0] || null : null;
      },

      async load({ id = null, slug = '' } = {}) {
        this.loading = true;
        this.error = '';

        const cacheKey = this.makeCacheKey({ id, slug });

        try {
          if (useCache && this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);

            this.query = {
              id: id || cached.id,
              slug: slug || cached.slug,
            };
            this.item = cached;
            this.open = true;
            return cached;
          }

          let page = null;

          if (id) {
            page = await this.fetchById(id);
          } else if (slug) {
            page = await this.fetchBySlug(slug);
          } else {
            throw new Error('id 또는 slug가 필요합니다.');
          }

          if (!page) {
            throw new Error('콘텐츠를 찾을 수 없습니다.');
          }

          const normalized = this.normalizeItem(page, slug);

          this.query = {
            id: normalized.id,
            slug: slug || normalized.slug,
          };

          this.item = normalized;
          this.open = true;

          if (useCache) {
            this.cache.set(cacheKey, normalized);

            if (normalized.id) {
              this.cache.set(
                this.makeCacheKey({ id: normalized.id }),
                normalized
              );
            }

            if (slug || normalized.slug) {
              this.cache.set(
                this.makeCacheKey({ slug: slug || normalized.slug }),
                normalized
              );
            }
          }

          return normalized;
        } catch (e) {
          this.error = e?.message || '콘텐츠를 불러오지 못했습니다.';
          this.item = {
            id: null,
            slug: '',
            title: '',
            content: '',
            raw: null,
          };
          return null;
        } finally {
          this.loading = false;
        }
      },

      async toggle({ id = null, slug = '' } = {}) {
        const sameById = id && this.query.id === id;
        const sameBySlug = slug && this.query.slug === slug;
        const sameTarget = sameById || sameBySlug;

        if (this.open && sameTarget) {
          this.open = false;
          return;
        }

        await this.load({ id, slug });
      },

      close() {
        this.open = false;
      },
    };
  };

  const locChargeKey = () => {
    const prefix =
      window.QRSHOP?.prefix ||
      window.QRSHOP_BASE?.prefix ||
      'qrshop:v1:global:';
    return `${prefix}ctx:locCharge`;
  };

  const readLocCharge = () => {
    try {
      return sessionStorage.getItem(locChargeKey()) || '';
    } catch {
      return '';
    }
  };

  const saveLocCharge = (value) => {
    try {
      const locCharge = String(value || '').trim();
      if (locCharge) {
        sessionStorage.setItem(locChargeKey(), locCharge);
      } else {
        sessionStorage.removeItem(locChargeKey());
      }
    } catch {}
  };

  // cart 내용 확인용
  window.cartModal = function () {
    return {
      locCharge: readLocCharge(),

      init() {
        this.locCharge = readLocCharge();
        this.$watch('locCharge', saveLocCharge);
        this.$watch('$store.modal.open', (open) => {
          if (open) this.locCharge = readLocCharge();
        });
      },

      list(type) {
        const items = this.$store.cart.items;
        return Array.isArray(items?.[type]) ? items[type] : [];
      },

      submit(event) {
        const formData = new FormData(event.target);
        saveLocCharge(this.locCharge);

        this.$store.request.submit('cart', {
          loc: formData.get('loc'),
          locCharge: this.locCharge,
        });
      },
    };
  };

  window.locationInput = function () {
    const prefix = window.QRSHOP_BASE?.prefix || 'qrshop:v1:global:';
    const KEY = prefix + 'ctx';
    const BACKUP_KEY = prefix + 'ctx:backup';
    const currentShop = window.QRSHOP_BASE?.shop || null;

    const readStorage = (storage, key) => {
      try {
        return storage.getItem(key) || '';
      } catch {
        return '';
      }
    };

    const writeStorage = (storage, key, value) => {
      try {
        storage.setItem(key, value);
      } catch {}
    };

    const parseJson = (raw) => {
      if (!raw) return null;

      try {
        return JSON.parse(raw) || null;
      } catch {
        return null;
      }
    };

    const readCookie = (name) => {
      try {
        const safe = String(name).replace(/[^a-zA-Z0-9_-]/g, '_') + '=';
        const parts = document.cookie ? document.cookie.split(';') : [];
        for (let i = 0; i < parts.length; i++) {
          const c = parts[i].trim();
          if (c.indexOf(safe) === 0) {
            return decodeURIComponent(c.substring(safe.length));
          }
        }
      } catch {}
      return '';
    };

    const isValidQrContext = (ctx) => {
      if (!ctx || typeof ctx !== 'object') return false;

      const shop = typeof ctx.shop === 'string' ? ctx.shop.trim() : '';
      const src = typeof ctx.src === 'string' ? ctx.src.trim() : '';
      const loc = typeof ctx.loc === 'string' ? ctx.loc.trim() : '';
      const expiresAt = Number(ctx.expires_at || 0);

      if (currentShop && shop && shop !== currentShop) return false;
      if (src !== 'qr' || !loc) return false;
      if (!expiresAt || expiresAt <= Date.now()) return false;

      return true;
    };

    const cacheQrLoc = (ctx) => {
      const loc = String(ctx.loc || '').trim();
      if (!loc || !document.body) return '';

      document.body.dataset.qrshopLoc = loc;
      document.body.dataset.qrshopLocLocked = '1';
      document.body.dataset.qrshopSrc = 'qr';
      return loc;
    };

    const readQrLoc = () => {
      const domLoc = document.body?.dataset?.qrshopLoc || '';
      if (domLoc) return domLoc;

      const sessionCtx = parseJson(readStorage(sessionStorage, KEY));
      if (isValidQrContext(sessionCtx)) {
        return cacheQrLoc(sessionCtx);
      }

      const backupCtx = parseJson(readStorage(localStorage, BACKUP_KEY));
      if (isValidQrContext(backupCtx)) {
        const restoredCtx = { ...backupCtx };
        delete restoredCtx.restore_until;
        writeStorage(sessionStorage, KEY, JSON.stringify(restoredCtx));
        if (restoredCtx.sid) {
          writeStorage(sessionStorage, prefix + 'sid', String(restoredCtx.sid));
        }

        return cacheQrLoc(restoredCtx);
      }

      // 추가 복구원: 쿠키 백업(인앱 웹뷰에서 session/localStorage 유실 대비)
      const cookieCtx = parseJson(readCookie(BACKUP_KEY));
      if (isValidQrContext(cookieCtx)) {
        const restoredCtx = { ...cookieCtx };
        delete restoredCtx.restore_until;
        writeStorage(sessionStorage, KEY, JSON.stringify(restoredCtx));
        if (restoredCtx.sid) {
          writeStorage(sessionStorage, prefix + 'sid', String(restoredCtx.sid));
        }

        return cacheQrLoc(restoredCtx);
      }

      return '';
    };

    return {
      loc: '',
      isLocked: false,
      get placeholder() {
        return 'QR 아래 번호';
      },
      readDefault() {
        const qrLoc = readQrLoc();
        this.isLocked = qrLoc !== '';

        return qrLoc;
      },
      init() {
        // 기본값으로 세팅됨
        this.loc = this.readDefault();

        // ✅ 모달이 열릴 때마다 기본값으로 리셋
        this.$watch('$store.modal.open', (open) => {
          if (open) {
            this.loc = this.readDefault();
          }
        });
      },
      save() {
        return;
      },
    };
  };

  window.callRequestForm = function () {
    return {
      request: '',
      locCharge: readLocCharge(),

      init() {
        this.locCharge = readLocCharge();
        this.$watch('locCharge', saveLocCharge);
        this.$watch('$store.modal.open', (open) => {
          if (open) {
            this.locCharge = readLocCharge();
            return;
          }
          this.request = '';
        });
      },

      submit(event) {
        const formData = new FormData(event.target);
        saveLocCharge(this.locCharge);

        this.$store.request
          .submit('call', {
            request: this.request,
            loc: formData.get('loc'),
            locCharge: this.locCharge,
          })
          .then((result) => {
            if (result) this.request = '';
          });
      },
    };
  };

  window.cartBounce = function () {
    return {
      bouncing: false,
      lastCount: 0,

      init() {
        // 초기값 동기화
        this.lastCount = this.$store.cart.totalCount;

        this.$watch(
          () => this.$store.cart.totalCount,
          (count) => {
            if (count !== this.lastCount) {
              this.trigger();
              this.lastCount = count;
            }
          }
        );
      },

      trigger() {
        this.bouncing = false; // 연속 트리거 대비
        requestAnimationFrame(() => {
          this.bouncing = true;
          setTimeout(() => {
            this.bouncing = false;
          }, 450); // CSS 애니메이션 시간과 맞추기
        });
      },
    };
  };

  Alpine.store(
    'request',
    createRequestStore({
      endpoint: (type) => {
        if (type === 'request') {
          return window.frontendAjaxObject.requestEndpoint; // 👉 request용
        }
        return window.frontendAjaxObject.commentEndpoint; // 👉 기존
      },
      nonce: window.frontendAjaxObject.restNonce,
    })
  );

  // 고객 요청 접수 상태 폴링(요청 전송 직후에만 시작)
  Alpine.store(
    'acceptStatus',
    createAcceptStatusStore({
      endpoint: window.frontendAjaxObject.requestStatusEndpoint,
    })
  );

  // 세션의 위치값

  Alpine.data(
    'requestForm',
    createRequestForm({
      endpoint: window.frontendAjaxObject.requestEndpoint,
      nonce: window.frontendAjaxObject.restNonce,
    })
  );

  Alpine.data('contentViewer', window.contentViewer);

  Alpine.data('initGlobe', initGlobe);
  Alpine.data('taxFilter', taxFilter);
  Alpine.data('dashboardStore', dashboardStore);
  Alpine.data('hairMBTI', hairMBTI);
  Alpine.data('shampooMBTI', shampooMBTI);
  Alpine.data('hairScholarQuiz', hairScholarQuiz);

  Alpine.magic('aos', () => ({
    refresh: () => AOS.refresh(),
  }));

  Alpine.start();

  initModalPostLinks();
  initModalProductCartButtons();

  // 로드 시점 QR loc 유실 계측(관찰 전용, 조건 미충족 시 아무 것도 안 함)
  Alpine.store('request')?.probeQrLocOnLoad?.();

  // 새로고침 전 접수 대기 중이던 요청이 있으면 타임아웃 창 안에서 폴링 재개
  Alpine.store('acceptStatus')?.resume?.();
}
